import { api } from "@/state/services";

/**
 * Utilitários do "Minhas Metas" (painel + wizard de planejamento).
 *
 * Regra de negócio (metas oficiais, gordura, premissas de faturamento) NÃO mora aqui: vem pronta
 * do GET metas/config (Model_minhas_metas no app_api). Este arquivo só formata e orquestra o
 * cache da configuração no Vuex.
 */

export const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export const OBJETIVOS = [
  {
    value: "meta",
    titulo: "Só bater a meta",
    texto: "Trazer o número exato da meta do fechamento.",
    icon: "bx bx-target-lock",
  },
  {
    value: "gordura",
    titulo: "Bater com gordura",
    texto: "Trazer um pouco a mais para absorver quem atrasar o 1º boleto.",
    icon: "bx bx-shield-quarter",
  },
  {
    value: "mais",
    titulo: "Trazer mais",
    texto: "Ir além da gordura e crescer a base mais rápido.",
    icon: "bx bx-rocket",
  },
];

export function nomeMes(mes) {
  return MESES[Number(mes) - 1] || "";
}

export function mesAno(mes, ano) {
  return nomeMes(mes) + "/" + ano;
}

/** 'Y-m-d' -> 'dd/mm/aaaa' (sem passar por Date, para não sofrer com fuso). */
export function fmtData(ymd) {
  if (!ymd || typeof ymd !== "string" || ymd.length < 10) return "—";
  return ymd.slice(8, 10) + "/" + ymd.slice(5, 7) + "/" + ymd.slice(0, 4);
}

export function fmtDataCurta(ymd) {
  if (!ymd || typeof ymd !== "string" || ymd.length < 10) return "—";
  return ymd.slice(8, 10) + "/" + ymd.slice(5, 7);
}

/** 'Y-m-d H:i:s' -> 'dd/mm/aaaa às HH:ii' */
export function fmtDataHora(valor) {
  if (!valor || typeof valor !== "string") return "";
  const data = fmtData(valor);
  const hora = valor.length >= 16 ? valor.slice(11, 16) : "";
  return hora ? data + " às " + hora : data;
}

const MOEDA = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
export function fmtMoeda(valor) {
  return MOEDA.format(Number(valor) || 0);
}

export function plural(n, singular, pluralTxt) {
  return Number(n) === 1 ? singular : pluralTxt;
}

/** Chave do cache por sessão (inclui a secretária, se houver). */
export function chaveUsuario(user) {
  if (!user || !user.id) return null;
  const ru = user.regional_user && user.regional_user.id ? ":ru" + user.regional_user.id : "";
  return String(user.id) + ru;
}

export function isSecretaria(user) {
  return !!(user && user.regional_user);
}

export function temApvs(user) {
  const m = (user && user.modules) || {};
  return m.apvs_brasil === "active" || m.apvs_truck === "active";
}

/**
 * Configuração do Minhas Metas com cache no Vuex (por usuário).
 * @returns {Promise<object>} corpo do GET metas/config
 */
export function carregarConfigMetas(store, opcoes) {
  const force = !!(opcoes && opcoes.force);
  const account = store.state.account;
  const key = chaveUsuario(account.user);

  if (!force && account.metasConfig && account.metasConfig.key === key) {
    return Promise.resolve(account.metasConfig.data);
  }

  return api.get("metas/config").then((response) => {
    const data = response && response.data;
    if (!data || data.status !== "success") {
      throw new Error((data && data.message) || "Não foi possível carregar o seu planejamento.");
    }
    store.commit("account/setMetasConfig", { key, data });
    return data;
  });
}

/** Produtos exibidos no painel, na ordem. */
export function produtosVisiveis(config) {
  if (!config) return [];
  const p = config.produtos || {};
  const plan = config.planejamento || {};
  const lista = [];
  if (p.brasil) lista.push("brasil");
  // Truck: módulo ativo E (marcou que produz, OU ainda não planejou, OU só tem Truck).
  if (p.truck && (plan.produz_truck || !plan.configurado || !p.brasil)) lista.push("truck");
  return lista;
}

/** Quantidade sugerida para o objetivo escolhido. */
export function sugestao(objetivo, regrasProduto) {
  if (!regrasProduto) return 0;
  if (objetivo === "meta") return regrasProduto.meta_oficial;
  if (objetivo === "mais") return Math.ceil(regrasProduto.recomendado * 1.3);
  return regrasProduto.recomendado;
}

/** Aviso para a quantidade escolhida (null = tudo certo). */
export function avisoQuantidade(valor, regrasProduto) {
  if (!regrasProduto) return null;
  const n = Number(valor);
  if (!Number.isFinite(n)) return null;
  if (n < regrasProduto.meta_oficial) {
    return {
      nivel: "danger",
      texto:
        "Abaixo da meta de " + regrasProduto.meta_oficial +
        ": com esse número você não bate a meta do fechamento.",
    };
  }
  if (n < regrasProduto.recomendado) {
    return {
      nivel: "warning",
      texto:
        "Bate a meta, mas sem gordura: se uma adesão atrasar o 1º boleto, você pode ficar fora. " +
        "O recomendado é " + regrasProduto.recomendado + ".",
    };
  }
  return null;
}

/** Classe do selo de situação de uma adesão (status.key do GET metas/painel). */
export function classeStatus(key) {
  return {
    contando: "mm-badge-ok",
    pago_aguardando: "mm-badge-info",
    pendente: "mm-badge-warn",
    estourado: "mm-badge-bad",
    cancelada: "mm-badge-neutral",
  }[key] || "mm-badge-neutral";
}

/** Texto do prazo de uma adesão do "Contam no Fechamento". */
export function textoPrazo(item) {
  const s = (item && item.status) || {};
  if (s.key === "contando") {
    return s.fechamento ? "Contou em " + nomeMes(s.fechamento.mes) : "—";
  }
  if (s.key === "estourado") return "Venceu em " + fmtData(s.limite_30);
  if (s.key === "pago_aguardando") return "Aguardando processamento";
  if (s.key === "pendente") {
    if (s.dias_prazo < 0) return "Prazo encerrado";
    if (s.dias_prazo === 0) return "Hoje é o último dia";
    return s.dias_prazo + " " + plural(s.dias_prazo, "dia", "dias") + " (até " + fmtDataCurta(s.prazo) + ")";
  }
  return "—";
}
